/**
 * Route table. Page modules stay lazy so each route loads its own bundle.
 * Provider setup lives in App.js.
 */
import { lazy, useMemo } from "react";
import { Navigate, Route, Routes, useParams, useSearchParams } from "react-router-dom";
import ProtectedRoute from "../components/navigation/ProtectedRoute";
import RoleBasedRoute from "../components/navigation/RoleBasedRoute";
import { PATHS, browsePath, PUBLIC_CATALOG_SLUGS } from "../config/routePaths";
import MainLayout from "../layouts/MainLayout";

const LEGACY_CATALOG_PATHS = Object.freeze(["poems", "stories", "novels", "books", "articles"]);

/** Old `/explore/:contentType?…` or legacy type paths → `/read` with query preserved */
function LegacyExploreRedirect() {
  const [searchParams] = useSearchParams();
  const target = useMemo(() => {
    const path = browsePath();
    const q = searchParams.toString();
    return q ? `${path}?${q}` : path;
  }, [searchParams]);
  return <Navigate to={target} replace />;
}

function RedirectToRead() {
  const [searchParams] = useSearchParams();
  const target = useMemo(() => {
    const q = searchParams.toString();
    return q ? `/read?${q}` : "/read";
  }, [searchParams]);
  return <Navigate to={target} replace />;
}

function LegacyContentViewRedirect() {
  const { id } = useParams();
  return <Navigate to={`/content/${id}/view`} replace />;
}

function LegacyContentEditRedirect() {
  const { id } = useParams();
  return <Navigate to={`/content/${id}/edit`} replace />;
}

const LandingPage = lazy(() => import("../features/landing/LandingPage"));
const PublicWritersPage = lazy(() => import("../features/landing/PublicWritersPage"));
const UnifiedAuthPage = lazy(() => import("../features/auth/UnifiedAuthPage"));
const ResetPasswordForm = lazy(() => import("../features/auth/ResetPasswordForm"));
const BecomeWriterPage = lazy(() => import("../features/auth/BecomeWriterPage"));

const RoleBasedDashboard = lazy(() => import("../components/navigation/RoleBasedDashboard"));
const UnifiedActivityPage = lazy(() => import("../features/activity/UnifiedActivityPage"));
const NotificationsPage = lazy(() => import("../features/notifications/NotificationsPage"));
const InterestSelection = lazy(() => import("../features/reader/InterestSelection"));
const SplitReaderDashboard = lazy(() => import("../features/reader/SplitReaderDashboard"));
const FollowingPage = lazy(() => import("../features/reader/FollowingPage"));
const LibraryPage = lazy(() => import("../features/reader/LibraryPage"));
const ReaderExplorePage = lazy(() => import("../features/reader/ReaderExplorePage"));
const ReaderSettingsPage = lazy(() => import("../features/reader/ReaderSettingsPage"));
const SubscriptionsPage = lazy(() => import("../features/subscriptions/SubscriptionsPage"));
const WriterSubscriptionTiersPage = lazy(() => import("../features/writer/WriterSubscriptionTiersPage"));
const ContentDetailsPage = lazy(() => import("../features/content/pages/ContentDetailsPage"));

const AdminDashboard = lazy(() => import("../features/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("../features/admin/AdminUsers"));
const ContentManagementPage = lazy(() => import("../features/content/pages/ContentManagementPage"));
const AdminReports = lazy(() => import("../features/admin/AdminReports"));
const AdminActivity = lazy(() => import("../features/admin/AdminActivity"));
const AdminSettings = lazy(() => import("../features/admin/AdminSettings"));
const BulkContentGenerator = lazy(() => import("../features/admin/BulkContentGenerator"));
const AdminEnquiries = lazy(() => import("../features/admin/AdminEnquiries"));
const AdminModeration = lazy(() => import("../features/admin/AdminModeration"));
const AdminRolesPermissions = lazy(() => import("../features/admin/AdminRolesPermissions"));
const AdminCategoriesTags = lazy(() => import("../features/admin/AdminCategoriesTags"));
const AdminAbuseSpam = lazy(() => import("../features/admin/AdminAbuseSpam"));
const AdminNotificationMgmt = lazy(() => import("../features/admin/AdminNotificationMgmt"));
const AdminCmsLanding = lazy(() => import("../features/admin/AdminCmsLanding"));
const AdminFeatureFlags = lazy(() => import("../features/admin/AdminFeatureFlags"));
const AdminSeoSettings = lazy(() => import("../features/admin/AdminSeoSettings"));
const AdminBackupData = lazy(() => import("../features/admin/AdminBackupData"));
const AdminFeedbackReviews = lazy(() => import("../features/admin/AdminFeedbackReviews"));

const UserProfile = lazy(() => import("../features/profile/UserProfile"));

const SearchResults = lazy(() => import("../features/search/SearchResults"));
const WritingPrompts = lazy(() => import("../features/community/WritingPrompts"));
const ReadingChallenges = lazy(() => import("../features/community/ReadingChallenges"));

const NotFound = lazy(() => import("../features/errors/NotFound"));
const TermsAndConditions = lazy(() => import("../features/static/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("../features/static/PrivacyPolicy"));
const ContactUs = lazy(() => import("../features/static/ContactUs"));
const CancellationPolicy = lazy(() => import("../features/static/CancellationPolicy"));
const AboutUs = lazy(() => import("../features/static/AboutUs"));
const ExploreContentPage = lazy(() => import("../features/explore/ExploreContentPage"));
const TrendingPage = lazy(() => import("../features/explore/TrendingPage"));
const PublicLayout = lazy(() => import("../layouts/PublicLayout"));
const Services = lazy(() => import("../features/static/Services"));
const Testimonials = lazy(() => import("../features/static/Testimonials"));
const WritingTips = lazy(() => import("../features/static/WritingTips"));
const Resources = lazy(() => import("../features/static/Resources"));
const Blog = lazy(() => import("../features/static/Blog"));
const WriterPricingPage = lazy(() => import("../features/static/WriterPricingPage"));

const LAYOUT_PROTECTED_DEFAULT = Object.freeze({
  showPublicHeader: false,
  showSidebar: false,
  showFooter: false,
  showSearch: true,
});

function ProtectedLayout({ children, config }) {
  return (
    <ProtectedRoute>
      <MainLayout config={config}>{children}</MainLayout>
    </ProtectedRoute>
  );
}

const withPublicLayout = (Component) => (
  <PublicLayout>
    <Component />
  </PublicLayout>
);

const withProtectedLayout = (Component) => (
  <ProtectedLayout config={LAYOUT_PROTECTED_DEFAULT}>
    <Component />
  </ProtectedLayout>
);

const withRoleLayout = (Component, allowedRoles) => (
  <ProtectedLayout config={LAYOUT_PROTECTED_DEFAULT}>
    <RoleBasedRoute allowedRoles={allowedRoles}>
      <Component />
    </RoleBasedRoute>
  </ProtectedLayout>
);

const PUBLIC_VIEW_PATHS = ["/content/:id/view"];

const WRITER_EDIT_PATHS = ["/content/:id/edit"];

const WRITER_NEW_PATHS = ["/content/new"];

const ADMIN_ROUTES = [
  { path: "/admin/dashboard", Component: AdminDashboard },
  { path: "/admin/users", Component: AdminUsers },
  { path: "/admin/reports", Component: AdminReports },
  { path: "/admin/activity", Component: AdminActivity },
  { path: "/admin/settings", Component: AdminSettings },
  { path: "/admin/bulk-generator", Component: BulkContentGenerator },
  { path: "/admin/enquiries", Component: AdminEnquiries },
  { path: "/admin/support", Component: AdminEnquiries },
  { path: "/admin/moderation", Component: AdminModeration },
  { path: "/admin/roles", Component: AdminRolesPermissions },
  { path: "/admin/categories-tags", Component: AdminCategoriesTags },
  { path: "/admin/abuse", Component: AdminAbuseSpam },
  { path: "/admin/notification-settings", Component: AdminNotificationMgmt },
  { path: "/admin/cms", Component: AdminCmsLanding },
  { path: "/admin/feature-flags", Component: AdminFeatureFlags },
  { path: "/admin/seo", Component: AdminSeoSettings },
  { path: "/admin/backup", Component: AdminBackupData },
  { path: "/admin/feedback", Component: AdminFeedbackReviews },
];

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/index.html" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<Navigate to="/auth/login" replace />} />
      <Route path="/register" element={<Navigate to="/auth/register?mode=register" replace />} />
      <Route path="/reader/login" element={<Navigate to="/auth/login" replace />} />
      <Route path="/reader/signup" element={<Navigate to="/auth/register?mode=register" replace />} />
      <Route path="/auth" element={<Navigate to="/auth/login" replace />} />
      <Route path="/auth/writer" element={<Navigate to="/auth/login" replace />} />
      <Route path="/auth/reader" element={<Navigate to="/auth/login" replace />} />

      <Route element={<PublicLayout />}>
        <Route path="/auth/login" element={<UnifiedAuthPage />} />
        <Route path="/auth/register" element={<UnifiedAuthPage initialRegister />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        <Route path="/become-writer" element={<BecomeWriterPage />} />
        <Route path="/writer-pricing" element={<WriterPricingPage />} />
        <Route path="/terms-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/cancellation-policy" element={<CancellationPolicy />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/services" element={<Services />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/writing-tips" element={<WritingTips />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/writing-prompts" element={<WritingPrompts />} />
        <Route path="/reading-challenges" element={<ReadingChallenges />} />
      </Route>

      {/* Public discovery & search */}
      <Route path="/browse" element={<Navigate to={PATHS.browse()} replace />} />
      <Route
        path="/trending"
        element={
          <PublicLayout>
            <TrendingPage />
          </PublicLayout>
        }
      />
      {PUBLIC_CATALOG_SLUGS.map((slug) => (
        <Route
          key={slug}
          path={`/${slug}`}
          element={
            <PublicLayout>
              <ExploreContentPage catalogSlug={slug} />
            </PublicLayout>
          }
        />
      ))}
      {LEGACY_CATALOG_PATHS.map((slug) => (
        <Route key={`legacy-${slug}`} path={`/${slug}`} element={<RedirectToRead />} />
      ))}
      <Route path="/explore" element={<Navigate to={PATHS.browse()} replace />} />
      <Route path="/explore/:contentType" element={<LegacyExploreRedirect />} />
      <Route
        path="/search"
        element={
          <PublicLayout>
            <SearchResults />
          </PublicLayout>
        }
      />
      <Route
        path="/writers"
        element={
          <PublicLayout>
            <PublicWritersPage />
          </PublicLayout>
        }
      />
      <Route path="/novels/new" element={<Navigate to="/content/new" replace />} />
      <Route path="/poems/new" element={<Navigate to="/content/new" replace />} />
      <Route path="/stories/new" element={<Navigate to="/content/new" replace />} />
      <Route path="/books/new" element={<Navigate to="/content/new" replace />} />
      <Route path="/novels/:id/view" element={<LegacyContentViewRedirect />} />
      <Route path="/novels/:id/edit" element={<LegacyContentEditRedirect />} />
      <Route path="/poems/:id/view" element={<LegacyContentViewRedirect />} />
      <Route path="/poems/:id/edit" element={<LegacyContentEditRedirect />} />
      <Route path="/stories/:id/view" element={<LegacyContentViewRedirect />} />
      <Route path="/stories/:id/edit" element={<LegacyContentEditRedirect />} />
      <Route path="/books/:id/view" element={<LegacyContentViewRedirect />} />
      <Route path="/books/:id/edit" element={<LegacyContentEditRedirect />} />

      {/* Writer create/edit — MUST be before :id/view so /content/new is not captured as id */}
      {WRITER_NEW_PATHS.map((path) => (
        <Route
          key={path}
          path={path}
          element={withRoleLayout(ContentDetailsPage, ["writer", "superadmin"])}
        />
      ))}
      {WRITER_EDIT_PATHS.map((path) => (
        <Route
          key={path}
          path={path}
          element={withRoleLayout(ContentDetailsPage, ["writer", "superadmin"])}
        />
      ))}

      {/* Public read (no login) */}
      {PUBLIC_VIEW_PATHS.map((path) => (
        <Route
          key={path}
          path={path}
          element={withPublicLayout(ContentDetailsPage)}
        />
      ))}

      {/* Public profiles (discovery) */}
      <Route
        path="/profile/:userId"
        element={
          <PublicLayout>
            <UserProfile />
          </PublicLayout>
        }
      />

      {/* ——— /app authenticated ——— */}
      <Route
        path="/app/dashboard"
        element={
          withProtectedLayout(RoleBasedDashboard)
        }
      />
      <Route
        path={PATHS.APP_SPLIT_READER_DASHBOARD}
        element={
          <ProtectedLayout config={LAYOUT_PROTECTED_DEFAULT}>
            <RoleBasedRoute allowedRoles={["reader"]}>
              <SplitReaderDashboard />
            </RoleBasedRoute>
          </ProtectedLayout>
        }
      />
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />

      <Route
        path="/app/activity"
        element={
          withProtectedLayout(UnifiedActivityPage)
        }
      />
      <Route path="/activity" element={<Navigate to="/app/activity" replace />} />
      <Route
        path="/app/notifications"
        element={
          withProtectedLayout(NotificationsPage)
        }
      />

      <Route
        path="/app/following"
        element={
          withProtectedLayout(FollowingPage)
        }
      />

      <Route
        path="/app/library"
        element={
          withProtectedLayout(LibraryPage)
        }
      />
      <Route
        path="/app/explore"
        element={
          withProtectedLayout(ReaderExplorePage)
        }
      />
      <Route
        path="/app/subscriptions"
        element={
          <ProtectedLayout config={LAYOUT_PROTECTED_DEFAULT}>
            <RoleBasedRoute allowedRoles={["reader", "writer", "superadmin"]}>
              <SubscriptionsPage />
            </RoleBasedRoute>
          </ProtectedLayout>
        }
      />
      <Route
        path="/app/settings"
        element={
          withProtectedLayout(ReaderSettingsPage)
        }
      />
      <Route path="/app/history" element={<Navigate to="/app/activity" replace />} />

      <Route path="/app/create" element={<Navigate to="/content/new" replace />} />
      <Route
        path="/app/writer-plans"
        element={<Navigate to={PATHS.APP_WRITER_SUBSCRIPTION} replace />}
      />
      <Route
        path="/app/writer-subscription"
        element={
          <ProtectedLayout config={LAYOUT_PROTECTED_DEFAULT}>
            <RoleBasedRoute allowedRoles={["writer", "superadmin"]}>
              <WriterSubscriptionTiersPage />
            </RoleBasedRoute>
          </ProtectedLayout>
        }
      />

      <Route
        path="/app/onboarding"
        element={
          <ProtectedLayout config={LAYOUT_PROTECTED_DEFAULT}>
            <RoleBasedRoute allowedRoles={["reader", "writer"]}>
              <InterestSelection />
            </RoleBasedRoute>
          </ProtectedLayout>
        }
      />
      <Route path="/reader/interests" element={<Navigate to="/app/onboarding" replace />} />

      <Route
        path="/app/content"
        element={
          <ProtectedLayout config={LAYOUT_PROTECTED_DEFAULT}>
            <RoleBasedRoute allowedRoles={["writer", "reader"]}>
              <ContentManagementPage initialScope="mine" allowScopeToggle />
            </RoleBasedRoute>
          </ProtectedLayout>
        }
      />
      <Route path="/content" element={<Navigate to="/app/content" replace />} />

      {/* Admin */}
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      {ADMIN_ROUTES.map(({ path, Component }) => (
        <Route
          key={path}
          path={path}
          element={withRoleLayout(Component, ["superAdmin", "superadmin"])}
        />
      ))}
      <Route
        path="/admin/content"
        element={
          withRoleLayout(ContentManagementPage, ["superAdmin", "superadmin"])
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
